import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BurgerMenu } from '@/components/kit';
import { useShellIdentity } from '@/components/shell/useShellIdentity';
import { useAuthStore } from '@/stores/useAuthStore';
import { useFeedbackStore } from '@/stores/useFeedbackStore';
import { UserRole } from '@/types/user';
import { useOrders, useShelf } from './lib/useStock';

/** Which door the screen opening the menu IS, so it can wear "Home". */
export type StockDoor =
  | 'shelf' | 'catalog' | 'receive' | 'orders' | 'count' | 'transfers' | 'directory';

/**
 * THE STOCK MANAGER'S ☰ — `maie-phone-stock-manager/g_k0`.
 *
 * SEVEN DOORS, the board's — the seventh is the Directory, suppliers only,
 * because receiving against a supplier is where a stock manager needs it.
 * The line under each is a live figure, read only while the menu is open.
 *
 * TWO ROLES, TWO GROUPS — the board's own subtitle. Whoever runs the shelf is
 * usually a nurse who also runs the shelf, so the nurse's two doors fold above
 * the stock ones rather than disappearing. The fold is absent for somebody who
 * only does stock, instead of showing an empty group.
 */
export function StockMenu({
  door,
  onClose,
  counts,
}: {
  door: StockDoor;
  onClose: () => void;
  counts?: Partial<Record<StockDoor, string>>;
}) {
  const navigate = useNavigate();
  const { person, branch } = useShellIdentity();
  const logout = useAuthStore((s) => s.logout);
  const roleCheck = useAuthStore((s) => s.roleCheck);
  const openFeedback = useFeedbackStore((s) => s.setOpen);
  const [groupOpen, setGroupOpen] = useState(false);

  /* A nurse who also runs the shelf — the common case this fold exists for.
     Asked by ROLE, not permission: the nurse's two doors are hers because of
     who she is, and there is no single permission that means "nurses". */
  const alsoNurse = roleCheck.hasRole([UserRole.NURSE]);

  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const home = <span className="chip chip-brand chip-sm">Home</span>;
  const { data: items = [] } = useShelf();
  const { data: orders = [] } = useOrders(false);
  const low = items.filter((i) => i.low).length;
  const drafts = orders.filter((o) => o.status === 'draft').length;
  const open = orders.filter((o) => o.status === 'ordered' || o.status === 'confirmed').length;
  const live = {
    shelf: items.length ? `the shelf · ${items.length} item${items.length === 1 ? '' : 's'}${low ? ` · ${low} low` : ''}` : 'the shelf',
    orders: drafts || open
      ? [drafts ? `${drafts} draft${drafts === 1 ? '' : 's'}` : '', open ? `${open} ordered` : ''].filter(Boolean).join(' · ')
      : 'MAIE reorders, you approve',
  };
  const at = (k: StockDoor) => (door === k ? home : undefined);

  return (
    <BurgerMenu
      open
      onClose={onClose}
      roleChip={alsoNurse ? 'Nurse · Stock manager' : 'Stock manager'}
      doors={[
        { key: 'shelf', icon: 'packages', label: 'Stock',
          sub: counts?.shelf ?? live.shelf, badge: at('shelf'),
          onSelect: () => go('/stock-manager/shelf') },
        { key: 'catalog', icon: 'book', label: 'Catalog',
          sub: counts?.catalog ?? 'definitions, not quantities', badge: at('catalog'),
          onSelect: () => go('/stock-manager/catalog') },
        { key: 'receive', icon: 'download', label: 'Receive',
          sub: counts?.receive ?? 'scan the invoice, confirm the lines', badge: at('receive'),
          onSelect: () => go('/stock-manager/receive') },
        { key: 'orders', icon: 'shopping-cart', label: 'Orders',
          sub: counts?.orders ?? live.orders, badge: at('orders'),
          onSelect: () => go('/stock-manager/orders') },
        { key: 'count', icon: 'list-check', label: 'Count',
          sub: counts?.count ?? 'the shelf walk', badge: at('count'),
          onSelect: () => go('/stock-manager/count') },
        { key: 'transfers', icon: 'arrows-exchange', label: 'Transfers',
          sub: counts?.transfers ?? 'balance the branches', badge: at('transfers'),
          onSelect: () => go('/stock-manager/transfers') },
        { key: 'directory', icon: 'address-book', label: 'Directory',
          sub: counts?.directory ?? 'suppliers only', badge: at('directory'),
          onSelect: () => go('/directory') },
      ]}
      group={
        alsoNurse
          ? {
              head: 'Nurse',
              summary: 'My day · Stock',
              caption: '2 doors · folded — you are on the shelf right now',
              items: [
                { key: 'n-day', label: 'My day', onSelect: () => go('/nurse/day') },
                { key: 'n-stock', label: 'Stock (read-only)', onSelect: () => go('/nurse/stock') },
              ],
            }
          : undefined
      }
      groupOpen={groupOpen}
      onToggleGroup={() => setGroupOpen((v) => !v)}
      note={<>An item opens from the shelf — its batches, its expiry and its own history.</>}
      account={{
        name: person?.name ?? 'This account',
        initials: person?.initials ?? 'SM',
        caption: 'account — devices · notifications',
        onSelect: () => go('/settings'),
      }}
      branch={branch}
      onAppearance={() => go('/settings#appearance')}
      onFeedback={() => { onClose(); openFeedback(true); }}
      onSignOut={() => void logout()}
    />
  );
}
